import React from 'react';
import { useParallax } from 'react-scroll-parallax';

// Parallax assets
const BUBBLES_BLUE =
  'https://santaplaya-bucket.s3.amazonaws.com/bubbles_blue.webp';
const BLUE_FISH = 'https://santaplaya-bucket.s3.amazonaws.com/blue_fish.webp';
const OCTOPUS = 'https://santaplaya-bucket.s3.amazonaws.com/blue_octopus.webp';
const PRAWN = 'https://santaplaya-bucket.s3.amazonaws.com/blue_prawn.webp';
const EEL = 'https://santaplaya-bucket.s3.amazonaws.com/eel.webp';

// Standard 20% service charge for demonstration
const SERVICE_CHARGE_RATE = 0.2;

// Our three main packages data
const PACKAGES = [
  {
    id: 'cocktail-party',
    name: 'Cocktail Party',
    tagline: 'Seafood-Inspired Appetizers & Small Bites',
    description: `
Perfect for a standing reception with a variety of seafood-inspired appetizers. Highlights include cold and hot botanas, plus shrimp cocktails from our extensive menu.
`,
    priceTiers: [
      { minGuests: 40, maxGuests: 99, pricePerPerson: 28 },
      { minGuests: 100, maxGuests: 199, pricePerPerson: 25 },
      { minGuests: 200, pricePerPerson: 22 },
    ],
    background: 'bg-white',
    textColor: 'text-basic',
  },
  {
    id: 'mariscos-fiesta',
    name: 'Mariscos Fiesta',
    tagline: 'Bold Baja Flavors, Seafood Favorites',
    description: `
An assortment of our most popular mariscos dishes, featuring ceviches, tostadas, and the signature Molcajete A La Cucaracha. Great for seafood lovers wanting vibrant Baja-style cuisine. 
`,
    priceTiers: [
      { minGuests: 40, maxGuests: 99, pricePerPerson: 25 },
      { minGuests: 100, maxGuests: 199, pricePerPerson: 23 },
      { minGuests: 200, pricePerPerson: 20 },
    ],
    background: 'bg-white',
    textColor: 'text-basic',
  },
  {
    id: 'fish-taco-fiesta',
    name: 'Fish Taco Fiesta',
    tagline: 'Baja-Style Fish Tacos + Optional Shrimp or Octopus',
    description: `
Enjoy our famous beer-battered fish tacos at a special price. 
Optionally upgrade to include shrimp or octopus for an extra charge.
`,
    priceTiers: [
      { minGuests: 40, maxGuests: 99, pricePerPerson: 20 },
      { minGuests: 100, maxGuests: 199, pricePerPerson: 18 },
      { minGuests: 200, pricePerPerson: 16 },
    ],
    background: 'bg-white',
    textColor: 'text-basic',
    upcharges: [
      { label: 'Add Shrimp', pricePerPerson: 2 },
      { label: 'Add Octopus', pricePerPerson: 4 },
      { label: 'Shrimp or Octopus Choice', pricePerPerson: 4 },
    ],
  },
];

const CateringPackages: React.FC = () => {
  // Parallax references
  // Fish on left: swims horizontally
  const fishLeftRef = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });
  // Fish on right: swims horizontally (opposite)
  const fishRightRef = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });
  // Bubbles: rising vertically
  const bubblesRef = useParallax<HTMLDivElement>({
    translateY: [40, -40],
    easing: 'easeInOut',
  });
  const bubblesRef1 = useParallax<HTMLDivElement>({
    translateY: [40, -40],
    easing: 'easeInOut',
  });
  const bubblesRef2 = useParallax<HTMLDivElement>({
    translateY: [40, -40],
    easing: 'easeInOut',
  });
  const bubblesRef3 = useParallax<HTMLDivElement>({
    translateY: [40, -40],
    easing: 'easeInOut',
  });
  const fishRightRef1 = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });
  const fishRightRef2 = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });
  const octopusRef = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });
  const prawnRef = useParallax<HTMLDivElement>({
    rotate: [20, -20],
    easing: 'easeInOut',
  });
  const eelRef = useParallax<HTMLDivElement>({
    translateX: [10, -20],
    easing: 'easeInOut',
  });

  return (
    <section className="relative w-full">
      {/* PARALLAX BACKGROUND ELEMENTS */}
      {/* Fish on the left */}
      <div
        ref={fishLeftRef.ref}
        className="
          pointer-events-none
          absolute
          -left-[12.5%]
          top-[30%]
          w-[35vw]
          z-0
        "
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Left"
          className="w-full h-auto scale-x-[-1]"
        />
      </div>
      {/* Fish on the right */}
      <div
        ref={fishRightRef.ref}
        className="
          pointer-events-none
          absolute
          -right-[12.5%]
          top-[40%]
          w-[35vw]
          z-0
        "
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Right"
          className="w-full h-auto scale-x-[-1]"
        />
      </div>
      {/* Bubbles near center, rising */}
      <div
        ref={bubblesRef.ref}
        className="
          pointer-events-none
          absolute
          right-[5%]
          top-[60%]
          w-[6vw]
          z-0
        "
      >
        <img
          src={BUBBLES_BLUE}
          alt="Bubbles Blue"
          className="w-full h-auto opacity-80"
        />
      </div>
      <div
        ref={bubblesRef1.ref}
        className="
          pointer-events-none
          absolute
          left-[5%]
          bottom-[20%]
          w-[6vw]
          z-0
        "
      >
        <img
          src={BUBBLES_BLUE}
          alt="Bubbles Blue"
          className="w-full h-auto opacity-80"
        />
      </div>
      <div
        ref={bubblesRef2.ref}
        className="
          pointer-events-none
          absolute
          left-[5%]
          top-0
          w-[6vw]
          z-0
        "
      >
        <img
          src={BUBBLES_BLUE}
          alt="Bubbles Blue"
          className="w-full h-auto opacity-80"
        />
      </div>
      <div
        ref={bubblesRef3.ref}
        className="
          pointer-events-none
          absolute
          right-[5%]
          top-[20%]
          w-[6vw]
          z-0
        "
      >
        <img
          src={BUBBLES_BLUE}
          alt="Bubbles Blue"
          className="w-full h-auto opacity-80"
        />
      </div>
      <div
        ref={fishRightRef1.ref}
        className="
          pointer-events-none
          absolute
          -left-[25%]
          bottom-0
          w-[35vw]
          z-0
        "
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Right 1"
          className="w-full h-auto scale-x-[-1]"
        />
      </div>
      <div
        ref={fishRightRef2.ref}
        className="
          pointer-events-none
          absolute
          -right-[25%]
          top-0
          w-[35vw]
          z-0
        "
      >
        <img
          src={BLUE_FISH}
          alt="Blue Fish Right 1"
          className="w-full h-auto scale-x-[-1]"
        />
      </div>
      <div
        ref={octopusRef.ref}
        className="
          pointer-events-none
          absolute
          -right-[15%]
          bottom-[15%]
          w-[35vw]
          z-0
        "
      >
        <img src={OCTOPUS} alt="Octopus" className="w-full h-auto" />
      </div>
      <div
        ref={eelRef.ref}
        className="
          pointer-events-none
          absolute
          -left-[20%]
          bottom-[45%]
          w-[35vw]
          z-0
        "
      >
        <img src={EEL} alt="Eel" className="w-full h-auto" />
      </div>
      <div
        ref={prawnRef.ref}
        className="
          pointer-events-none
          absolute
          left-[15%]
          top-[15%]
          w-[10vw]
          z-0
        "
      >
        <img
          src={PRAWN}
          alt="Prawn"
          className="w-full h-auto transform rotate-[140deg]"
        />
      </div>

      {/* FOREGROUND: The existing package content */}
      <div className="w-full">
        {PACKAGES.map((pkg) => (
          <div
            key={pkg.id}
            // This ID is crucial for QuickNav scroll
            id={pkg.id}
            className={`${pkg.background} ${pkg.textColor} py-12 px-4`}
          >
            <div className="max-w-5xl mx-auto flex flex-col items-center">
              {/* Ribbon + Heading */}
              <div className="relative flex justify-center items-center pt-20 pb-10">
                <img
                  src="https://santaplaya-bucket.s3.amazonaws.com/ribbon_highlight.webp"
                  alt="Left Ribbon"
                  className="
                    relative
                    mr-4
                    transform 
                    scale-x-[-1]
                    w-[18vw]
                    md:w-[8vw]
                  "
                />
                <h2 className="text-3xl md:text-5xl xl:text-7xl text-maroon text-center font-charles !leading-tight">
                  {pkg.name.split(' ').map((word, idx) => (
                    <React.Fragment key={idx}>
                      {word}
                      <br />
                    </React.Fragment>
                  ))}
                </h2>
                <img
                  src="https://santaplaya-bucket.s3.amazonaws.com/ribbon_highlight.webp"
                  alt="Right Ribbon"
                  className="
                    relative
                    ml-4
                    w-[18vw]
                    md:w-[8vw]
                  "
                />
              </div>
              {pkg.tagline && (
                <h3 className="font-prosto text-lg md:text-xl mb-4 italic text-sea">
                  {pkg.tagline}
                </h3>
              )}

              {/* Description */}
              <p className="font-prosto text-base md:text-lg text-center max-w-3xl mb-6 whitespace-pre-line">
                {pkg.description.trim()}
              </p>

              {/* Price Tiers */}
              <h4 className="font-charles text-xl text-maroon mb-3">
                Price Tiers
              </h4>
              <ul className="font-prosto text-base text-sea mb-6 space-y-1">
                {pkg.priceTiers.map((tier, i) => {
                  const range = tier.maxGuests
                    ? `${tier.minGuests}-${tier.maxGuests} guests`
                    : `${tier.minGuests}+ guests`;
                  return (
                    <li key={i}>
                      <strong>{range}:</strong> $
                      {tier.pricePerPerson.toFixed(2)} per person
                    </li>
                  );
                })}
              </ul>

              {/* Possible Upcharges (e.g. shrimp, octopus) */}
              {pkg.upcharges && pkg.upcharges.length > 0 && (
                <div className="mb-6">
                  <h5 className="font-charles text-lg text-maroon mb-2">
                    Optional Upgrades
                  </h5>
                  <ul className="font-prosto text-base space-y-1">
                    {pkg.upcharges.map((up, i) => (
                      <li key={i}>
                        {up.label}: +${up.pricePerPerson.toFixed(2)} / person
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="font-prosto text-sm italic mb-10 text-gray-700">
                * A {(SERVICE_CHARGE_RATE * 100).toFixed(0)}% service charge
                will be added. 40-person minimum. Discounted rates for 100+ and
                200+ guests.
              </p>
            </div>

            {/* Additional Info: 3 columns (Service Provided, Meal Options, Coordination) */}
            <div className="max-w-5xl mx-auto px-4 md:grid md:grid-cols-3 gap-6">
              {/* Column 1: Service Provided */}
              <div className="mb-8">
                <h5 className="font-charles text-lg mb-2 text-maroon">
                  Service Provided
                </h5>
                <p className="font-prosto text-base leading-relaxed text-sea">
                  {pkg.id === 'cocktail-party' &&
                    `
                    We set up a reception-style station with cold & hot mariscos-based appetizers 
                    and small plates. Perfect for social mixers where guests graze at leisure.
                  `}
                  {pkg.id === 'mariscos-fiesta' &&
                    `
                    Buffet-style service featuring our signature seafood dishes. Our team arrives 
                    early to prepare, serve, and replenish trays as needed.
                  `}
                  {pkg.id === 'fish-taco-fiesta' &&
                    `
                    Buffet or live taco station. We fry fish on-site for maximum freshness. 
                    Staff ensures tortillas, toppings, and salsas remain stocked throughout service.
                  `}
                </p>
              </div>

              {/* Column 2: Meal Options */}
              <div className="mb-8">
                <h5 className="font-charles text-lg mb-2 text-maroon">
                  Meal Options
                </h5>
                <p className="font-prosto text-base leading-relaxed text-sea">
                  {pkg.id === 'cocktail-party' &&
                    `
                    Choose from a variety of BOTANAS (Frío & Caliente) plus Seafood Cocktails. 
                    Ideal for walking and talking, with no formal plated meals. 
                  `}
                  {pkg.id === 'mariscos-fiesta' &&
                    `
                    Ceviche Samplers, Molcajete A La Cucaracha, Tostadas, Rice, Beans, and more. 
                    Customize with mild or spicy salsas, add tortillas, or combine with 
                    other dishes from the menu.
                  `}
                  {pkg.id === 'fish-taco-fiesta' &&
                    `
                    Base package covers fish tacos. Optionally add shrimp or octopus. 
                    Accompanied by rice, beans, and a range of house salsas. 
                  `}
                </p>
              </div>

              {/* Column 3: Coordination */}
              <div className="mb-8">
                <h5 className="font-charles text-lg mb-2 text-maroon">
                  Coordination
                </h5>
                <p className="font-prosto text-base leading-relaxed text-sea">
                  {pkg.id === 'cocktail-party' &&
                    `
                    Perfect for a casual, upbeat atmosphere. 
                    We typically staff 1 server per ~25 guests, ensuring trays remain full.
                  `}
                  {pkg.id === 'mariscos-fiesta' &&
                    `
                    We coordinate timing with your venue or schedule. Our staff 
                    handles setup, serving, and teardown within a 2-hour service window (unless arranged otherwise).
                  `}
                  {pkg.id === 'fish-taco-fiesta' &&
                    `
                    We'll bring fryers and stations for fish (and shrimp/octopus if selected). 
                    Service generally lasts 2 hours, with staff on hand to keep everything fresh.
                  `}
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-center items-center mt-24">
              <img
                src="https://santaplaya-bucket.s3.amazonaws.com/seashellBorder.webp"
                className="w-1/2"
                alt="Seashell Border"
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CateringPackages;
